import { useState, useEffect } from "react";
import { PodcastPlaybackProgressService } from "../podcastPlaybackProgress.service";

interface ListenedTimeStats {
	totalSeconds: number;
	totalMinutes: number;
	totalHours: number;
	formattedTime: string;
}

/**
 * Hook to fetch and format the user's total listened time across all podcasts
 *
 * @returns Object containing total listened time in various formats
 */
export function useListenedTime(): {
	stats: ListenedTimeStats;
	isLoading: boolean;
	error: Error | null;
	refetch: () => Promise<void>;
} {
	const [stats, setStats] = useState<ListenedTimeStats>({
		totalSeconds: 0,
		totalMinutes: 0,
		totalHours: 0,
		formattedTime: "0m",
	});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const fetchListenedTime = async () => {
		try {
			setIsLoading(true);
			setError(null);

			// Fetch recent listening history (get more to calculate total time)
			const recentListens =
				await PodcastPlaybackProgressService.getRecentListens(100);

			// Calculate total listened time from all segments
			let totalSeconds = 0;

			for (const progress of recentListens) {
				if (progress.listenedSegments && progress.listenedSegments.length > 0) {
					const duration =
						PodcastPlaybackProgressService.calculateListenedDuration(
							progress.listenedSegments,
						);
					totalSeconds += duration;
				}
			}

			const totalMinutes = Math.floor(totalSeconds / 60);
			const totalHours = totalMinutes / 60;
			const formattedTime = formatListenedTime(totalSeconds);

			setStats({
				totalSeconds,
				totalMinutes,
				totalHours,
				formattedTime,
			});
		} catch (err) {
			setError(
				err instanceof Error ? err : new Error("Failed to fetch listened time"),
			);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchListenedTime();
	}, []);

	return {
		stats,
		isLoading,
		error,
		refetch: fetchListenedTime,
	};
}

/**
 * Format listened time in a human-readable format
 * Examples: "5m", "1h 23m", "12h 45m"
 */
function formatListenedTime(seconds: number): string {
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;

	if (hours === 0) {
		return `${minutes}m`;
	}

	if (remainingMinutes === 0) {
		return `${hours}h`;
	}

	return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format listened time with full labels
 * Examples: "5 minutes", "1 hour 23 minutes", "12 hours 45 minutes"
 */
export function formatListenedTimeLong(seconds: number): string {
	if (seconds < 60) {
		const s = Math.floor(seconds);
		return `${s} second${s !== 1 ? "s" : ""}`;
	}

	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;

	if (hours === 0) {
		return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
	}

	if (remainingMinutes === 0) {
		return `${hours} hour${hours !== 1 ? "s" : ""}`;
	}

	return `${hours} hour${hours !== 1 ? "s" : ""} ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`;
}
